import {
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { Theme, withStyles } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import { IconProp } from 'app/AllIcons';
import { TFunctionResult } from 'i18next';
import { translations } from 'locales/translations';
import {
  bindMenu,
  usePopupState,
  bindTrigger,
  anchorRef,
  PopupState,
} from 'material-ui-popup-state/hooks';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { PluginHook } from 'react-table';
import { IPathAndQuery, toRootedURL } from 'utils/url-utils';
import { IRow } from '.';
import { RouterLink } from '../BasicLinks/RouterLink';
import { IconButton } from '../BasicButtons/IconButton';
import { Icon } from '../BasicIcons/FontAwesome';
import { Tooltip } from '../BasicTooltips/Tooltip';
import { CellRenderer } from './BasicTableProps';
import { ButtonsVariant } from '../BasicButtons/Button';
import { omit } from 'lodash';
import BasicTypography from '../Typography/BasicTypography';

export interface RowActionsProps {
  href?: string | IPathAndQuery;
  to?: string;
  text: string;
  icon: IconProp;
  title?: string;
  target?: string;
  renderAsTitle?: boolean;
  localizedText?: string;
  onClick?: () => Promise<void>;
  withLoading?: boolean;
}
export interface RowActionsMenuProps {
  id: string;
  items: RowActionsProps[];
  icon?: IconProp;
  tooltip?: React.ReactNode | TFunctionResult;
  variant?: ButtonsVariant;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  getIsOpen?: () => boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));
const StyledItemIcon = withStyles({
  root: {
    minWidth: 'unset',
  },
})(ListItemIcon);
const RowMenuActions = React.forwardRef<any, RowActionsMenuProps>(
  function RowMenuActions(props, ref) {
    const { id, items, variant, icon, tooltip, onClick } = props;
    const classes = useStyles();
    const popupState = usePopupState({
      variant: 'popper',
      popupId: id,
    });
    const [tooltipOpen, setTooltipOpen] = React.useState(false);
    React.useEffect(() => {
      if (popupState.isOpen) {
        setTooltipOpen(false);
      }
    }, [popupState.isOpen]);
    const popupProps = { ...bindTrigger(popupState) };
    return (
      <div ref={anchorRef(popupState)} className={classes.root}>
        {!!tooltip ? (
          <Tooltip
            title={tooltip}
            open={tooltipOpen}
            onOpen={() => {
              setTooltipOpen(!popupState.isOpen);
            }}
            onClose={() => setTooltipOpen(false)}
          >
            <span>
              <IconButton
                variant={variant || 'ghost'}
                shape="square"
                aria-label="Open Menu"
                size="xs"
                onClick={event => {
                  popupProps.onClick(event);
                  if (!!onClick) {
                    onClick(event);
                  }
                }}
                {...omit(popupProps, 'onClick')}
              >
                <Icon icon={icon ?? 'ellipsis-v'} />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <IconButton
            variant={variant || 'ghost'}
            shape="square"
            aria-label="Open Menu"
            size="xs"
            onClick={event => {
              popupProps.onClick(event);
              if (!!onClick) {
                onClick(event);
              }
            }}
            {...omit(popupProps, 'onClick')}
          >
            <Icon icon={icon ?? 'ellipsis-v'} />
          </IconButton>
        )}

        <Menu
          {...bindMenu(popupState)}
          ref={ref}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          getContentAnchorEl={null}
        >
          {items.map((item, index) => (
            <RowAction
              key={index}
              popupState={popupState}
              ref={ref}
              {...item}
            />
          ))}
        </Menu>
      </div>
    );
  },
);

const toAbsolutePath = (href: string | IPathAndQuery) => {
  return typeof href === 'string'
    ? toRootedURL(href)
    : toRootedURL((href as IPathAndQuery).path, (href as IPathAndQuery).search);
};

const RowAction = ({
  ref,
  popupState,
  ...item
}: RowActionsProps & {
  ref: React.ForwardedRef<any>;
  popupState: PopupState;
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const setProcessing = React.useCallback(
    (processing: boolean) => {
      if (!item.withLoading) return;
      setLoading(processing);
    },
    [item.withLoading],
  );
  if (item.renderAsTitle)
    return (
      <MenuItem dense component={'li'} ref={ref}>
        {/* <StyledItemIcon>
        <Icon icon={item.icon} />
      </StyledItemIcon> */}
        <BasicTypography variant="boldS">
          {!!item.localizedText ? t(item.localizedText) : item.text}
        </BasicTypography>
        {/* <ListItemText>
        {!!item.localizedText ? t(item.localizedText) : item.text}
      </ListItemText> */}
      </MenuItem>
    );
  if (item.href)
    return (
      <MenuItem
        dense
        //href={toAbsolutePath(item.href)}
        component={'li'}
        ref={ref}
        onClick={() => {
          if (item.href !== undefined) {
            let url = toAbsolutePath(item.href);
            window.open(url, '_blank');
          }
          popupState.close();
        }}
      >
        <StyledItemIcon>
          <Icon icon={item.icon} />
        </StyledItemIcon>
        <ListItemText>{item.text}</ListItemText>
      </MenuItem>
    );
  if (item.to) {
    return (
      <MenuItem
        dense
        component={RouterLink}
        to={item.to}
        ref={ref}
        onClick={popupState.close}
      >
        <StyledItemIcon>
          <Icon icon={item.icon} />
        </StyledItemIcon>
        <ListItemText>{item.text}</ListItemText>
      </MenuItem>
    );
  }
  return (
    <MenuItem
      dense
      ref={ref}
      disabled={loading}
      onClick={async () => {
        if (!item.withLoading) popupState.close();
        if (item.onClick) {
          setProcessing(true);
          await item.onClick();
          setProcessing(false);
          if (item.withLoading) popupState.close();
        }
      }}
    >
      <StyledItemIcon>
        <Icon icon={item.icon} />
      </StyledItemIcon>
      <ListItemText>{item.text}</ListItemText>
      {loading && <CircularProgress size={16} />}
    </MenuItem>
  );
};

export function RowActionsMenu(props: RowActionsMenuProps) {
  return <RowMenuActions {...props} />;
}
export function useRowActionsColumn<TRow extends IRow>(
  rowActions: CellRenderer<TRow>[] | undefined,
  tablePlugins: PluginHook<TRow>[],
) {
  const { t } = useTranslation();
  if (rowActions !== undefined) {
    if (rowActions.length > 0) {
      const actionColumns = rowActions?.map((column, index) => ({
        id: `ActionColumn_${index}`,
        Header: t(translations.Action),
        disableSortBy: true,
        Cell: column,
      }));
      tablePlugins.push(hooks => {
        hooks.visibleColumns.push(columns => [...columns, ...actionColumns]);
      });
    }
  }
}
export function useInlineRowActionsColumn<TRow extends IRow>(
  inlineRowActions: CellRenderer<TRow>[] | undefined,
  tablePlugins: PluginHook<TRow>[],
) {
  const { t } = useTranslation();
  if (inlineRowActions !== undefined) {
    if (inlineRowActions.length > 0) {
      const actionColumns = inlineRowActions?.map((column, index) => ({
        id: `InlineActionColumn_${index}`,
        Header: t(translations.Action),
        disableSortBy: true,
        Cell: column,
      }));
      tablePlugins.push(hooks => {
        hooks.visibleColumns.push(columns => [...columns, ...actionColumns]);
      });
    }
  }
}
